import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Download, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import { FormattedText } from "../portal/FormattedText";
import { downloadFile } from "../portal/SubmissionHistory";

const card = "rounded-2xl border border-[#E5E7EB] bg-white";
const TZ = "Europe/Bucharest";
const when = (iso) => (iso ? new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
  .format(new Date(iso)) : "");
const FILTERS = [["pending", "În așteptare"], ["all", "Toate"]];

/**
 * "Cererile formatorilor" (28.10.2026, decision 12): a trainer's version of a
 * library assignment for their group, approved or refused here, with a reason.
 * The library itself never changes from a request.
 */
export default function ChangeRequests({ onBack }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/admin/portal/change-requests", { params: { status } }).then((r) => setData(r.data))
      .catch((err) => setError(apiErrorMessage(err, "Nu am putut încărca cererile.")));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid gap-6" data-testid="admin-change-requests">
      <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#965EA7]">
        <ArrowLeft size={14} /> Toate grupele
      </button>
      <div>
        <h2 className="font-display text-2xl font-bold text-[#373A45]">Cererile formatorilor</h2>
        <p className="mt-1 max-w-3xl text-sm text-[#6B7280]">
          Un formator cere altă cerință pentru o temă din biblioteca cursului, doar pentru grupa lui. Aprobată, grupa primește
          varianta lui; biblioteca rămâne cum e. Respinsă, formatorul vede motivul.
        </p>
      </div>
      <div className="flex gap-2">
        {FILTERS.map(([v, label]) => (
          <button key={v} type="button" onClick={() => setStatus(v)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${status === v ? "bg-[#373A45] text-white" : "border border-[#E5E7EB] text-[#373A45]"}`}>
            {label}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-[#c0483f]">{error}</p>}
      {!data && !error && <Loader2 className="mx-auto animate-spin text-[#6B7280]" />}
      {data && data.requests.length === 0 && <p className="text-sm text-[#6B7280]">Nicio cerere.</p>}
      {data && data.requests.map((r) => <RequestCard key={r.id} r={r} onDone={load} />)}
    </div>
  );
}

function RequestCard({ r, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (approve) => {
    if (approve && r.has_hand_ins && !window.confirm("Unii cursanți au predat deja tema. Aprobi totuși noua cerință?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/portal/change-requests/${r.id}/decide`, { approve, reason });
      toast.success(approve ? "Cererea e aprobată: grupa are varianta formatorului." : "Cererea e respinsă.");
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut salva răspunsul."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={`${card} grid gap-3 p-5`} data-testid={`admin-change-request-${r.id}`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{r.group_name}</p>
        <h3 className="font-semibold text-[#373A45]">{r.title}</h3>
        <p className="text-xs text-[#6B7280]">
          {r.requested_by} · {when(r.requested_at)} · {r.status_label}
          {r.decided_by ? ` · ${r.decided_by}, ${when(r.decided_at)}` : ""}
        </p>
      </div>
      <p className="text-sm text-[#373A45]"><strong>Motivul formatorului:</strong> {r.reason}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <Version title="Acum (din bibliotecă)" v={r.current} />
        <Version title="Varianta formatorului" v={r.proposed} highlight />
      </div>
      {r.has_hand_ins && r.status === "pending" && (
        <p className="flex items-center gap-1.5 text-xs text-[#8a5d12]">
          <AlertTriangle size={13} /> Unii cursanți au predat deja tema, pe cerința de acum.
        </p>
      )}
      {r.decision_reason && <p className="text-sm text-[#373A45]"><strong>Răspunsul echipei:</strong> {r.decision_reason}</p>}
      {r.status === "pending" && (
        <div className="grid gap-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={1000}
            placeholder="Motivul (obligatoriu la respingere)"
            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm focus:border-[#965EA7] focus:outline-none" />
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => decide(true)} data-testid={`admin-change-approve-${r.id}`}
              className="rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50">
              Aprobă
            </button>
            <button type="button" disabled={busy || !reason.trim()} onClick={() => decide(false)}
              className="rounded-full border border-[#E5E7EB] px-4 py-2 text-sm font-semibold text-[#373A45] hover:border-[#c0483f] hover:text-[#a33a32] disabled:opacity-50">
              Respinge
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Version({ title, v, highlight = false }) {
  return (
    <div className={`rounded-xl p-3 ${highlight ? "bg-[#F0E7F4]" : "bg-[#F7F7F8]"}`}>
      <p className="text-xs font-semibold text-[#373A45]">{title}</p>
      {v.description ? <FormattedText text={v.description} className="mt-1 text-sm text-[#373A45]" />
        : <p className="mt-1 text-sm text-[#373A45]">—</p>}
      {(v.links || []).length > 0 && (
        <ul className="mt-2 grid gap-1 text-xs">
          {v.links.map((l) => <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" className="text-[#965EA7] hover:underline">{l.label || l.url}</a></li>)}
        </ul>
      )}
      {(v.files || []).length > 0 && (
        <ul className="mt-2 grid gap-1">
          {v.files.map((f) => (
            <li key={f.id}>
              <button type="button" onClick={() => downloadFile(f.id, (m) => toast.error(m), "/admin/portal")}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#373A45] hover:text-[#965EA7]">
                <Download size={12} /> {f.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
